import React from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Text, TextLink } from '@frontend/design-system';

type CorrectionBannerProps = {
  canMakeCorrection?: boolean;
  isReviewed: boolean;
  onMakeCorrection?: () => void;
  onViewEditHistory?: () => void;
  showEditHistory?: boolean;
  trackingIds?: {
    correctionBtn?: string;
    viewHistory?: string;
  };
};

export const CorrectionBanner: React.FC<CorrectionBannerProps> = ({
  canMakeCorrection = false,
  isReviewed = false,
  onMakeCorrection,
  onViewEditHistory,
  showEditHistory = false,
  trackingIds,
}) => {
  const { t } = useTranslation('analytics');

  const isInteractive = canMakeCorrection || showEditHistory;

  return (
    <div css={wrapperStyles({ isInteractive })}>
      <Text size='small' css={styles.noWrap}>
        {isReviewed ? t('Call reviewed.') : t('Is this analysis accurate?')}
      </Text>

      <div css={styles.actions}>
        {canMakeCorrection && (
          <TextLink
            as='p'
            css={styles.actionBtn}
            size='small'
            weight='bold'
            onClick={onMakeCorrection}
            trackingId={trackingIds?.correctionBtn}
          >
            {t('Make a Correction')}
          </TextLink>
        )}
        {showEditHistory && canMakeCorrection && <div css={styles.divider} />}
        {showEditHistory && (
          <Button
            iconName='history-small'
            size='small'
            variant='tertiary'
            onClick={onViewEditHistory}
            css={styles.historyBtn}
            data-testid='call-analysis-view-edit-history-btn'
          />
        )}
      </div>
    </div>
  );
};

const wrapperStyles = ({ isInteractive }: { isInteractive: boolean }) => css`
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.neutral20};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing(1)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${theme.shadows.light};
  flex-wrap: wrap;

  width: ${isInteractive ? '100%' : 'max-content'};
  @media screen and (max-width: ${breakpoints.medium.min}px) {
    gap: ${isInteractive ? theme.spacing(0.5) : theme.spacing(0)};
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.neutral20};
    box-shadow: ${theme.shadows.light};
    width: min-content;
  }
`;

const styles = {
  actionBtn: css`
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    &:hover {
      text-decoration: none;
    }
  `,
  actions: css`
    display: flex;
    gap: ${theme.spacing(0.5)};
    align-items: center;
  `,
  divider: css`
    border: 1px solid ${theme.colors.neutral20};
    height: ${theme.spacing(2)};
  `,
  noWrap: css`
    white-space: nowrap;
  `,

  historyBtn: css`
    padding: 0;
    height: ${theme.spacing(1.5)};
    width: ${theme.spacing(1.5)};
    color: ${theme.colors.neutral70};

    &:hover {
      color: ${theme.colors.neutral70};
      background: ${theme.colors.white};
    }

    &:focus {
      color: ${theme.colors.neutral70};
      background: ${theme.colors.white};
    }

    svg {
      height: ${theme.spacing(1.5)};
      width: ${theme.spacing(1.5)};
      color: ${theme.colors.neutral70};
    }
  `,
};
